/**
 * Finance V2 — Pro Forma / Scenario Modeling.
 * Base/Upside/Downside/Custom + editable inputs + monthly deploy/recover schedule.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, CategoryChip, fmtMoney0, fmtPct, fmtNum, NotConfiguredNotice } from "./_shared";

const SCENARIOS = ["base", "upside", "downside", "custom"];

const emptyInputs = {
  cases_per_month_start: 30,
  monthly_growth_pct: 2.0,
  avg_advance: 2500,
  additional_advance_rate: 0.10,
  avg_additional_advance: 1500,
  additional_advance_delay_months: 4,
  loss_rate: 0.05,
  partial_recovery_pct: 0.20,
  reduction_rate: 0.03,
  attorney_acquisition_per_month: 2.0,
  repeat_funding_rate: 0.30,
  horizon_months: 36,
};

export default function FinanceProForma() {
  const [scenario, setScenario] = useState("base");
  const [inputs, setInputs] = useState(emptyInputs);
  const [structures, setStructures] = useState([]);
  const [structId, setStructId] = useState("");
  const [result, setResult] = useState(null);
  const [msv, setMsv] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: presets } = await api.get("/finance/v2/pro-forma/presets");
        setInputs({ ...emptyInputs, ...(presets.base || {}) });
      } catch (e) { /* noop */ }
      try {
        const { data } = await api.get("/finance/v2/capital-structures");
        setStructures(data.structures || []);
        const current = (data.structures || []).find(s => s.is_current);
        if (current) setStructId(current.id);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const loadPreset = async (name) => {
    setScenario(name);
    if (name === "custom") return;
    try {
      const { data: presets } = await api.get("/finance/v2/pro-forma/presets");
      const p = presets[name];
      if (p) setInputs({ ...emptyInputs, ...p });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/finance/v2/pro-forma/run", {
        scenario, inputs, capital_structure_id: structId || null,
      });
      setResult(data);
    } catch (e) {
      // Backend returns 422 with NOT_CONFIGURED_MESSAGE when no structure
      const msg = formatApiError(e);
      if (/no capital structure/i.test(msg)) {
        setResult(null);   // triggers the NotConfiguredNotice
      } else {
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  const runMSV = async () => {
    try {
      const { data } = await api.post("/finance/v2/max-sustainable-volume", {
        scenario, inputs, capital_structure_id: structId || null,
      });
      setMsv(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [scenario, structId]);

  const kpis = result?.kpis || {};
  const rows = result?.monthly_schedule || [];

  return (
    <div className="p-6 max-w-[1400px]" data-testid="finance-pro-forma-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Pro Forma</h1>
          <div className="text-xs text-[#6B7280] mt-1">36-month deterministic cohort simulation. Every output is Forecast (Modeled).</div>
        </div>
        <div className="flex items-center gap-2">
          <CategoryChip category="forecast" />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Scenario</label>
          <div className="flex gap-1" data-testid="scenario-tabs">
            {SCENARIOS.map((s) => (
              <button key={s} onClick={() => loadPreset(s)} data-testid={`scenario-${s}`}
                      className={`h-9 px-3 text-sm rounded-md border ${scenario === s ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white border-[#E5E7EB]"}`}>
                {s[0].toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Capital structure</label>
          <select value={structId} onChange={(e) => setStructId(e.target.value)}
                  className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="structure-select">
            <option value="">— Select a Capital Structure —</option>
            {structures.map((s) => (
              <option key={s.id} value={s.id}>{s.name} — {s.annual_cost_of_capital_pct}%</option>
            ))}
          </select>
        </div>
        <button onClick={run} disabled={loading}
                className="h-9 px-4 rounded-md bg-[#0A2540] text-white text-sm font-medium disabled:opacity-50"
                data-testid="run-btn">
          {loading ? "Running…" : "Run scenario"}
        </button>
        <button onClick={runMSV}
                className="h-9 px-4 rounded-md bg-white border border-[#E5E7EB] text-sm font-medium"
                data-testid="msv-btn">Max sustainable volume</button>
      </div>

      {!result && !loading && (
        <NotConfiguredNotice />
      )}

      <Section title="Assumptions" category="forecast" subtitle="Edit any value; click Run to recompute.">
        <div className="grid grid-cols-4 gap-3">
          {Object.entries({
            cases_per_month_start: "Cases funded / mo (start)",
            monthly_growth_pct: "Monthly growth (%)",
            avg_advance: "Avg advance ($)",
            additional_advance_rate: "Additional advance rate",
            avg_additional_advance: "Avg additional advance ($)",
            loss_rate: "Loss rate",
            partial_recovery_pct: "Partial recovery",
            reduction_rate: "Reduction rate",
            attorney_acquisition_per_month: "Attorney acquisition / mo",
            repeat_funding_rate: "Repeat funding rate",
            horizon_months: "Horizon (months)",
          }).map(([k, label]) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">{label}</label>
              <input type="number" step="any" value={inputs[k]}
                     onChange={(e) => { setScenario("custom"); setInputs({ ...inputs, [k]: parseFloat(e.target.value || 0) }); }}
                     className="h-9 w-full px-3 text-sm border border-[#E5E7EB] rounded-md bg-white tabular-nums"
                     data-testid={`input-${k}`} />
            </div>
          ))}
        </div>
      </Section>

      {result && (<>
      <Section title="Projection KPIs" category="forecast">
        <div className="grid grid-cols-4 gap-3">
          <Kpi label="Total capital deployed" value={fmtMoney0(kpis.total_capital_deployed)} />
          <Kpi label="Total capital recovered" value={fmtMoney0(kpis.total_capital_recovered)} />
          <Kpi label="Gross realized revenue" value={fmtMoney0(kpis.total_gross_realized_funding_revenue)} />
          <Kpi label="Total losses" value={fmtMoney0(kpis.total_principal_losses)} negative />
          <Kpi label="Total cost of capital" value={fmtMoney0(kpis.total_cost_of_capital)} />
          <Kpi label="Net portfolio contribution" value={fmtMoney0(kpis.net_portfolio_contribution)} accent testid="kpi-net" />
          <Kpi label="Contribution margin" value={fmtPct(kpis.contribution_margin_pct)} />
          <Kpi label="Return on deployed" value={fmtPct(kpis.return_on_deployed_capital)} />
          <Kpi label="Peak outstanding" value={fmtMoney0(kpis.peak_outstanding)} sub={kpis.peak_month} />
          <Kpi label="Recycling ratio" value={kpis.capital_recycling_ratio?.toFixed(2) || "—"} />
          <Kpi label="Ending outstanding" value={fmtMoney0(kpis.ending_outstanding)} />
        </div>
        {result?.capital_status && (
          <div className="mt-4 text-xs text-[#4B5563]">
            {result.capital_status.unconstrained ? "Unconstrained (no facility cap on selected structure)." : (
              <>Facility {fmtMoney0(result.capital_status.facility_amount)} · Peak utilization {fmtPct((result.capital_status.peak_utilization_pct) || 0)} · Months with shortfall: {result.capital_status.months_with_shortfall}</>
            )}
          </div>
        )}
      </Section>

      {msv && (
        <Section title="Max sustainable funding volume" category="forecast"
                  subtitle="Binary search on cases/mo that keeps peak outstanding ≤ facility.">
          <div className="grid grid-cols-4 gap-3">
            <Kpi label="Facility" value={fmtMoney0(msv.facility_amount)} />
            <Kpi label="Max cases / month (start)" value={fmtNum(msv.max_cases_per_month_start)} testid="msv-cases" />
            <Kpi label="Resulting peak" value={fmtMoney0(msv.resulting_peak_outstanding)} />
            <Kpi label="Resulting net contribution" value={fmtMoney0(msv.resulting_net_contribution)} />
          </div>
        </Section>
      )}

      <Section title="Monthly Deploy / Recover Schedule" category="forecast">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB] sticky top-0 bg-white">
                <th className="py-2">Month</th>
                <th className="text-right">Begin Cap</th>
                <th className="text-right">Original</th>
                <th className="text-right">Addl</th>
                <th className="text-right">Deployed</th>
                <th className="text-right">Recovered</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Losses</th>
                <th className="text-right">CoC</th>
                <th className="text-right">Net Contrib</th>
                <th className="text-right">End Cap</th>
                <th className="text-right">Outstanding</th>
                <th className="text-right">Shortfall</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month_index} className="border-b border-[#F3F4F6]" data-testid={`month-${r.label}`}>
                  <td className="py-1.5">{r.label}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.beginning_available_capital)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.original_advances_deployed)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.additional_advances_deployed)}</td>
                  <td className="tabular-nums text-right font-medium">{fmtMoney0(r.total_deployed)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.capital_recovered)}</td>
                  <td className="tabular-nums text-right text-[#065F46]">{fmtMoney0(r.funding_revenue_collected)}</td>
                  <td className="tabular-nums text-right text-[#991B1B]">{fmtMoney0(r.losses)}</td>
                  <td className="tabular-nums text-right text-[#92400E]">{fmtMoney0(r.cost_of_capital)}</td>
                  <td className="tabular-nums text-right font-medium">{fmtMoney0(r.net_portfolio_contribution)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.ending_available_capital)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.outstanding_portfolio)}</td>
                  <td className="tabular-nums text-right text-[#991B1B]">{r.capital_shortfall > 0 ? fmtMoney0(r.capital_shortfall) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      </>)}
    </div>
  );
}
